import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { combineLatest, Observable, Subject, takeUntil } from 'rxjs';
import { Store, Select } from '@ngxs/store';
import {
  ApiPatientRequest,
  Patient,
  TestPatient,
  TestPatientCopayExclusionOption,
  TestPatientDeductibleOverrideOption,
  TestPatientNotificationOption,
  TestPatientPaymentMethodOption,
} from '../core/models';
import {
  LoadTestPatient,
  FetchTestPatients,
  UpdateTestPatient,
} from '../core/store/test-patients/test-patients.actions';
import { TestPatientsState } from '../core/store/test-patients/test-patients.state';
import { Router } from '@angular/router';
import { EnvService } from '../core/services';
import { minExclusiveValidator, validateAllFormFields } from '../core/utils/validators';
import { FormFieldOption } from '../shared/form-field/form-field.component';
import { ApiStoreData, CcsSentryError } from '../core/store/utils';
import { asFormControl, join } from '../core/utils/common';
import { omit } from 'lodash-es';

enum UpdatePatientAction {
  AcceptTermsAndConditions,
  RevokeTermsAndConditions,
  ConsentToMarketing,
  RevokeMarketingConsent,
}

@Component({
  selector: 'app-test-patients',
  templateUrl: './test-patients.component.html',
  styleUrls: ['./test-patients.component.scss'],
})
export class TestPatientsComponent implements OnInit {
  @Select(TestPatientsState.isLoading) isLoading$!: Observable<boolean>;
  @Select(TestPatientsState.error) error$!: Observable<CcsSentryError | null>;
  @Select(TestPatientsState.testPatients) testPatients$!: Observable<TestPatient[]>;
  @Select(TestPatientsState.updateTestPatientsState) updateTestPatientsState$!: Observable<
    ApiStoreData<Patient | null> & { patientId: number }
  >;
  @Select(TestPatientsState.testPatientId) testPatientId$!: Observable<number | null>;
  @Select(TestPatientsState.testPatientLoading) testPatientLoading$!: Observable<boolean>;
  @Select(TestPatientsState.loadTestPatientErrors) loadTestPatientErrors$!: Observable<CcsSentryError[] | null>;

  asFormControl = asFormControl;
  isApiMocked: boolean = this.envService.api.useMock;

  paymentMethodOptions: FormFieldOption[] = [
    { label: '1 Credit Card', value: TestPatientPaymentMethodOption.OneCreditCard },
    { label: '0 Payment Methods', value: TestPatientPaymentMethodOption.NoPaymentMethods },
    { label: '1 Expired Credit Card', value: TestPatientPaymentMethodOption.OneExpiredCreditCard },
    { label: '1 Valid & 1 Expired Credit Card', value: TestPatientPaymentMethodOption.OneValidAndOneExpiredCreditCard },
    { label: '1 Credit Card & 1 Bank Account', value: TestPatientPaymentMethodOption.OneCreditCardAndOneBankAccount },
    { label: 'Do Nothing', value: TestPatientPaymentMethodOption.DoNothing },
  ];

  notificationOptions: FormFieldOption[] = [
    { label: 'Do Nothing', value: TestPatientNotificationOption.DoNothing },
    { label: 'No Notifications', value: TestPatientNotificationOption.NoNotifications },
    { label: 'Display Notification', value: TestPatientNotificationOption.DisplayNotification },
    {
      label: 'Action Notification With One Button',
      value: TestPatientNotificationOption.ActionNotificationWithOneButton,
    },
    {
      label: 'Action Notification With Two Buttons',
      value: TestPatientNotificationOption.ActionNotificationWithTwoButtons,
    },
    { label: 'Expiration Notification', value: TestPatientNotificationOption.ExpirationNotification },
    { label: 'cgm90 Enrollment Notification', value: TestPatientNotificationOption.Cgm90EnrollmentNotification },
    { label: 'Two Action Notifications', value: TestPatientNotificationOption.TwoActionNotifications },
  ];

  copayExclusionOptions: FormFieldOption[] = [
    { label: 'Do nothing', value: TestPatientCopayExclusionOption.DoNothing },
    { label: 'Add to Copay Exclusion', value: TestPatientCopayExclusionOption.AddToExclusionList },
    { label: 'Remove from Copay Exclusion', value: TestPatientCopayExclusionOption.RemoveFromExclusionList },
  ];

  deductibleOverrideOptions: FormFieldOption[] = [
    { label: 'Do nothing', value: TestPatientDeductibleOverrideOption.DoNothing },
    {
      label: 'Primary and Secondary Allowed',
      value: TestPatientDeductibleOverrideOption.PrimaryAllowedSecondaryAllowed,
    },
    {
      label: 'Primary Allowed (Secondary Disallowed)',
      value: TestPatientDeductibleOverrideOption.PrimaryAllowedSecondaryDisallowed,
    },
    {
      label: 'Secondary Allowed (Primary Disallowed)',
      value: TestPatientDeductibleOverrideOption.PrimaryDisallowedSecondaryAllowed,
    },
    {
      label: 'Primary and Secondary Disallowed',
      value: TestPatientDeductibleOverrideOption.PrimaryDisallowedSecondaryDisallowed,
    },
  ];

  patientForm = this.formBuilder.group({
    patientId: [null, [Validators.required, minExclusiveValidator(0)]],
    paymentMethodOption: this.paymentMethodOptions[0].value,
    notificationOption: this.notificationOptions[0].value,
    copayExclusionOption: this.copayExclusionOptions[0].value,
    deductibleOverrideOption: this.deductibleOverrideOptions[0].value,
  });

  patientResultsFormLookup: { [key: number]: FormGroup } = {};

  findTestPatientsForm: FormGroup = this.formBuilder.group({});

  selectButtonOptions: FormFieldOption[] = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
    { label: 'Not Selected', value: null },
  ];
  mismatchedShipDatesOptions: FormFieldOption[] = this.selectButtonOptions.filter(({ label }) => label !== 'No');

  insuranceTypeOptions = [
    { label: 'Medicare', value: 1 },
    { label: 'Medicare Replacement', value: 2 },
    { label: 'Medicaid', value: 3 },
    { label: 'Commercial', value: 4 },
  ];

  selectedInsuranceTypes = this.insuranceTypeOptions.map((o) => o.value);

  shipmentTypeOptions = [
    { label: 'Diabetic', value: 10 },
    { label: 'Respiratory', value: 20 },
    { label: 'Ostomy', value: 30 },
    //{ label: 'Equipment Rentals', value: 40 }, // may not need
    //{ label: 'Enteral', value: 50 }, // may not need
    //{ label: 'Foot Care', value: 70 }, // may not need
    { label: 'Urological', value: 80 },
    { label: 'Prescription', value: 100 },
    //{ label: 'Samples', value: 110 }, // may not need
    //{ label: 'Lab Kit', value: 120 }, // may not need
    //{ label: 'Woundcare', value: 130 }, // may not need
    { label: 'Incontinence', value: 140 },
    //{ label: 'Misc. Equipment & Supplies', value: 150 }, // may not need
    //{ label: 'CPAP', value: 160 }, // may not need
    //{ label: 'Woundcare Pump', value: 170 }, // may not need
    //{ label: 'My CareTeam', value: 200 }, // may not need
    //{ label: 'PT/INR', value: 210 }, // may not need
    //{ label: 'Orthotics', value: 220 }, // may not need
  ];

  existingShipmentTypeOptions = [{ label: 'Pump and/or CGM', value: 60 }];

  selectedShipmentTypes = this.shipmentTypeOptions.map((o) => o.value);
  selectedExistingShipmentTypes = this.existingShipmentTypeOptions.map((o) => o.value);

  UpdatePatientAction = UpdatePatientAction;

  loadTestPatientErrorMessage: string | null = null;

  constructor(
    private store: Store,
    private router: Router,
    private envService: EnvService,
    private formBuilder: FormBuilder
  ) {
    this.resetFindTestPatientsForm();
    this.setHasReordersListener();
    this.setShipmentTypesListener();
  }

  ngOnInit(): void {
    if (this.isApiMocked) this.store.dispatch(new FetchTestPatients());
    this.testPatients$.subscribe((tps) => {
      if (tps) {
        this.patientResultsFormLookup = {};
        tps.forEach(
          (tp) =>
            (this.patientResultsFormLookup[tp.id] = this.formBuilder.group({
              paymentMethodOption: this.paymentMethodOptions[0].value,
              notificationOption: this.notificationOptions[0].value,
              copayExclusionOption: this.copayExclusionOptions[0].value,
              deductibleOverrideOption: this.deductibleOverrideOptions[0].value,
            }))
        );
      }
    });
  }

  loadAppWithPatient(
    id: number,
    paymentMethodOption: TestPatientPaymentMethodOption,
    notificationOption: TestPatientNotificationOption,
    copayExclusionOption: TestPatientCopayExclusionOption,
    deductibleOverrideOption: TestPatientDeductibleOverrideOption,
    url: string[] = ['/']
  ) {
    const testPatientSignedIn = new Subject<void>();
    this.store
      .dispatch(
        new LoadTestPatient(
          this.envService.test.basicToken,
          id,
          paymentMethodOption,
          notificationOption,
          copayExclusionOption,
          deductibleOverrideOption
        )
      )
      .subscribe(() => {
        combineLatest([this.testPatientLoading$, this.loadTestPatientErrors$])
          .pipe(takeUntil(testPatientSignedIn))
          .subscribe(([tpl, ltpe]) => {
            if (!tpl && !ltpe) {
              this.loadTestPatientErrorMessage = null;
              testPatientSignedIn.next();
              this.router.navigate(url);
            } else if (!!ltpe) {
              const errorMsgs = ltpe.map((e) => e.uiMessage).filter((msg) => !!msg) as string[];
              this.loadTestPatientErrorMessage = join(errorMsgs) || 'Unknown Error';
            }
          });
      });
  }

  toggleMock() {
    this.envService.api.useMock = !this.envService.api.useMock;
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
    this.router.navigate([this.router.url]);
  }

  onSubmitPatientForm() {
    if (!this.patientForm.valid) {
      validateAllFormFields(this.patientForm);
      return;
    }

    const { patientId, paymentMethodOption, notificationOption, copayExclusionOption, deductibleOverrideOption } =
      this.patientForm.value;
    this.loadAppWithPatient(
      patientId,
      paymentMethodOption,
      notificationOption,
      copayExclusionOption,
      deductibleOverrideOption
    );
  }

  resetFindTestPatientsForm() {
    const defaultTestPatientsFormValues = {
      minNumOfInsurances: 1,
      maxNumOfInsurances: 10,
      insuranceTypeIds: [this.selectedInsuranceTypes],
      minNumOfProviders: 1,
      maxNumOfProviders: 10,
      minNumOfAddresses: 1,
      maxNumOfAddresses: 30,
      minNumOfPastOrders: 1,
      maxNumOfPastOrders: 10000,
      hasReorders: true,
      minNumOfReorders: [1, Validators.min(1)],
      maxNumOfReorders: [10000, Validators.min(1)],
      authorizeViaExistingShipments: true,
      existingShipmentTypeIds: [this.selectedExistingShipmentTypes],
      shipmentTypeIds: [],
      minNumOfReorderItems: 1,
      maxNumOfReorderItems: 100,
      minOutstandingBalance: 0,
      maxOutstandingBalance: 10000,
      limit: 5,
      targetShipDateDaysBack: 3,
      targetShipDateDaysForward: 3,
      hasPaymentPlan: null,
      hasRentalSchedule: null,
      hasLastProviderVisit: null,
      hasDaysOnHandRequired: null,
      hasTestingFrequencyRequired: null,
      hasPumpSiteChangeFrequencyRequired: null,
      hasMismatchingTargetShipDates: null,
      hasNoChargeItems: null,
      hasMissingDocumentation: null,
      hasSetsOrReservoirs: null,
      hasSensors: null,
      hasTestingSupplies: null,
      isPortalUser: null,
      isNewLeafPatient: null,
    };
    this.findTestPatientsForm = this.formBuilder.group(defaultTestPatientsFormValues);
    this.findTestPatientsForm.get('shipmentTypeIds')?.disable();
  }

  setHasReordersListener() {
    this.findTestPatientsForm.get('hasReorders')?.valueChanges.subscribe((hr) => {
      if (hr) {
        this.findTestPatientsForm.get('minNumOfReorders')?.setValidators(Validators.min(1));
        this.findTestPatientsForm.get('maxNumOfReorders')?.setValidators(Validators.min(1));
        this.findTestPatientsForm.patchValue({ minNumOfReorders: 1, maxNumOfReorders: 10000 });
      } else {
        this.findTestPatientsForm.get('minNumOfReorders')?.clearValidators();
        this.findTestPatientsForm.get('maxNumOfReorders')?.clearValidators();
        this.findTestPatientsForm.patchValue({ minNumOfReorders: 0, maxNumOfReorders: 0 });
      }
      this.findTestPatientsForm.get('minNumOfReorders')?.updateValueAndValidity();
      this.findTestPatientsForm.get('maxNumOfReorders')?.updateValueAndValidity();
    });
  }

  setShipmentTypesListener() {
    this.findTestPatientsForm.get('authorizeViaExistingShipments')?.valueChanges.subscribe((useExistingShipments) => {
      if (useExistingShipments) {
        this.findTestPatientsForm.get('existingShipmentTypeIds')?.setValue(this.selectedExistingShipmentTypes);
        this.findTestPatientsForm.get('existingShipmentTypeIds')?.enable();
        this.findTestPatientsForm.get('shipmentTypeIds')?.setValue([]);
        this.findTestPatientsForm.get('shipmentTypeIds')?.disable();
      } else {
        this.findTestPatientsForm.get('existingShipmentTypeIds')?.setValue([]);
        this.findTestPatientsForm.get('existingShipmentTypeIds')?.disable();
        this.findTestPatientsForm.get('shipmentTypeIds')?.setValue(this.selectedShipmentTypes);
        this.findTestPatientsForm.get('shipmentTypeIds')?.enable();
      }
    });
  }

  onSubmitFindPatient() {
    if (this.findTestPatientsForm.valid) {
      const authorizeViaExistingShipments = this.findTestPatientsForm.value.authorizeViaExistingShipments;
      const existingShipmentTypeIdsValue = this.findTestPatientsForm.value.existingShipmentTypeIds;
      const shipmentTypeIdsValue = this.findTestPatientsForm.value.shipmentTypeIds;

      this.store.dispatch(
        new FetchTestPatients({
          ...omit(this.findTestPatientsForm.value, [
            'authorizeViaExistingShipments',
            'existingShipmentTypeIds',
            'shipmentTypeIds',
          ]),
          existingShipmentTypeIds: existingShipmentTypeIdsValue,
          shipmentTypeIds: shipmentTypeIdsValue,
        })
      );
    } else {
      validateAllFormFields(this.findTestPatientsForm);
    }
  }

  onClickPatientLink(patientId: number) {
    const { paymentMethodOption, notificationOption, copayExclusionOption, deductibleOverrideOption } =
      this.patientResultsFormLookup[patientId].value;
    this.loadAppWithPatient(
      patientId,
      paymentMethodOption,
      notificationOption,
      copayExclusionOption,
      deductibleOverrideOption
    );
  }

  onClickReorderLink(patientId: number, reorderId: number) {
    const { paymentMethodOption, notificationOption, copayExclusionOption, deductibleOverrideOption } =
      this.patientResultsFormLookup[patientId].value;
    this.loadAppWithPatient(
      patientId,
      paymentMethodOption,
      notificationOption,
      copayExclusionOption,
      deductibleOverrideOption,
      ['orders', reorderId.toString(), 'authorize']
    );
  }

  onUpdatePatient(event: any, patientId: number, action: UpdatePatientAction) {
    let updates: ApiPatientRequest = {};

    switch (action) {
      case UpdatePatientAction.AcceptTermsAndConditions:
        updates = { acceptedTermsAndConditions: true };
        break;
      case UpdatePatientAction.RevokeTermsAndConditions:
        updates = { acceptedTermsAndConditions: false };
        break;
      case UpdatePatientAction.ConsentToMarketing:
        updates = { consentedToMarketing: true };
        break;
      case UpdatePatientAction.RevokeMarketingConsent:
        updates = { consentedToMarketing: false };
        break;
    }

    this.store.dispatch(new UpdateTestPatient(patientId, updates));
  }

  getTestPatientTnC(patientId: number): string {
    const testPatientState = this.store
      .selectSnapshot(TestPatientsState.updateTestPatientsState)
      .find((s) => s.patientId === patientId);

    return !testPatientState
      ? 'Unknown'
      : testPatientState.isUpdating
      ? 'Updating'
      : testPatientState.error
      ? 'Error'
      : testPatientState.data?.acceptedTermsAndConditions
      ? 'true'
      : 'false';
  }

  getTestPatientMarketingConsent(patientId: number): string {
    const testPatientState = this.store
      .selectSnapshot(TestPatientsState.updateTestPatientsState)
      .find((s) => s.patientId === patientId);

    return !testPatientState
      ? 'Unknown'
      : testPatientState.isUpdating
      ? 'Updating'
      : testPatientState.error
      ? 'Error'
      : testPatientState.data?.consentedToMarketing
      ? 'true'
      : 'false';
  }
}
